import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  SimpleGrid,
  Spinner,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useToast, // Add this import
  Image,
} from '@chakra-ui/react';
import { useAuth } from '../context/AuthContext';
import { UpstoxDataService } from '../services/upstoxData';
import { Holding } from '../types/upstox';
import { CandleChart } from '../components/CandleChart';
import { CandleInterval } from '../services/upstoxData';
import { UpstoxAuthService } from '../services/upstoxAuth';
import { upstoxConfig } from '../config/upstox';

const PortfolioSummary = ({ holdings }: { holdings: Holding[] }) => {
  const totalValue = holdings.reduce((sum, h) => sum + (h.last_price * h.quantity), 0);
  const totalPnL = holdings.reduce((sum, h) => sum + h.pnl, 0);
  const pnlPercentage = (totalPnL / (totalValue - totalPnL)) * 100;

  return (
    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} mb={6}>
      <Stat
        p={4}
        shadow="lg"
        border="1px solid"
        borderColor={useColorModeValue('gray.200', 'gray.600')}
        borderRadius="lg"
      >
        <StatLabel>Portfolio Value</StatLabel>
        <StatNumber>{new Intl.NumberFormat('en-IN', {
          style: 'currency',
          currency: 'INR'
        }).format(totalValue)}</StatNumber>
      </Stat>
      <Stat
        p={4}
        shadow="lg"
        border="1px solid"
        borderColor={useColorModeValue('gray.200', 'gray.600')}
        borderRadius="lg"
      >
        <StatLabel>Total P&L</StatLabel>
        <StatNumber color={totalPnL >= 0 ? 'green.500' : 'red.500'}>
          {new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR'
          }).format(totalPnL)}
        </StatNumber>
        <StatHelpText>
          <StatArrow type={pnlPercentage >= 0 ? 'increase' : 'decrease'} />
          {Math.abs(pnlPercentage).toFixed(2)}%
        </StatHelpText>
      </Stat>
      <Stat
        p={4}
        shadow="lg"
        border="1px solid"
        borderColor={useColorModeValue('gray.200', 'gray.600')}
        borderRadius="lg"
      >
        <StatLabel>Holdings</StatLabel>
        <StatNumber>{holdings.length}</StatNumber>
      </Stat>
    </SimpleGrid>
  );
};

interface CandleData {
  timestamp: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  oi: number;
}

export const Dashboard: React.FC = () => {
  const { upstoxToken, setUpstoxToken } = useAuth();
  const toast = useToast();
  
  const [holdings, setHoldings] = useState<Holding[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedStock, setSelectedStock] = useState<Holding | null>(null);
  const [candleData, setCandleData] = useState<CandleData[]>([]);
  const [candleInterval, setCandleInterval] = useState<CandleInterval>('day');
  const [isChartLoading, setIsChartLoading] = useState(false);

  const HoldingsTable = ({ holdings }: { holdings: Holding[] }) => {
    const bgColor = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.600');

    const formatCurrency = (value: number) => {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR'
      }).format(value);
    };

    return (
      <Box
        overflowX="auto"
        boxShadow="lg"
        borderRadius="lg"
        bg={bgColor}
        borderWidth="1px"
        borderColor={borderColor}
      >
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>Company</Th>
              <Th>Quantity</Th>
              <Th>Avg Price</Th>
              <Th>Current Price</Th>
              <Th>P&L</Th>
              <Th>Day Change</Th>
              <Th>Value</Th>
            </Tr>
          </Thead>
          <Tbody>
            {holdings.map((holding) => (
              <Tr 
                key={holding.isin} 
                onClick={() => handleStockClick(holding)}
                cursor="pointer"
                _hover={{ bg: useColorModeValue('gray.50', 'gray.700') }}
              >
                <Td>
                  <Text fontWeight="bold">{holding.company_name}</Text>
                  <Text fontSize="sm" color="gray.500">{holding.trading_symbol}</Text>
                </Td>
                <Td>{holding.quantity}</Td>
                <Td>{formatCurrency(holding.average_price)}</Td>
                <Td>{formatCurrency(holding.last_price)}</Td>
                <Td>
                  <Text color={holding.pnl >= 0 ? 'green.500' : 'red.500'} fontWeight="bold">
                    {formatCurrency(holding.pnl)}
                  </Text>
                </Td>
                <Td>
                  <Badge
                    colorScheme={holding.day_change >= 0 ? 'green' : 'red'}
                    display="flex"
                    alignItems="center"
                    width="fit-content"
                  >
                    <Stat>
                      <StatArrow type={holding.day_change >= 0 ? 'increase' : 'decrease'} />
                    </Stat>
                    {Math.abs(holding.day_change_percentage).toFixed(2)}%
                  </Badge>
                </Td>
                <Td>{formatCurrency(holding.last_price * holding.quantity)}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    );
  };

  const handleHoldingsError = (error: unknown) => {
    if (error instanceof Error && error.message === 'SESSION_EXPIRED') {
      setUpstoxToken('');
      toast({
        title: 'Session Expired',
        description: 'Please reconnect your Upstox account',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
    } else {
      toast({
        title: 'Error fetching holdings',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const fetchCandleData = async (stock: Holding, interval: CandleInterval) => {
    try {
      setIsChartLoading(true);
      const upstoxData = new UpstoxDataService(upstoxToken!);
      
      const instrumentKey = `${stock.exchange}_EQ|${stock.isin}`;
      const toDate = new Date().toISOString().split('T')[0];
      const fromDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      
      const data = await upstoxData.getCandleData(
        instrumentKey,
        interval,
        toDate,
        fromDate
      );
      setCandleData(data);
    } catch (error) {
      handleHoldingsError(error);
    } finally {
      setIsChartLoading(false);
    }
  };

  const handleStockClick = (stock: Holding) => {
    setSelectedStock(stock);
    fetchCandleData(stock, candleInterval);
  };

  const handleIntervalChange = (interval: CandleInterval) => {
    setCandleInterval(interval);
    if (selectedStock) {
      fetchCandleData(selectedStock, interval);
    }
  };

  const connectUpstox = () => {
    const authService = new UpstoxAuthService(upstoxConfig);
    authService.initiateAuth();
  };

  useEffect(() => {
    if (upstoxToken) {
      const fetchHoldings = async () => {
        try {
          setIsLoading(true);
          const upstoxData = new UpstoxDataService(upstoxToken);
          const { data } = await upstoxData.getHoldings();
          console.log('Fetched holdings:', data); // Added log
          setHoldings(data);
        } catch (error) {
          handleHoldingsError(error);
        } finally {
          setIsLoading(false);
        }
      };
      fetchHoldings();
    }
  }, [upstoxToken, toast]);

  return (
    <Container maxW="container.xl" py={8}>
      {/* Remove Header and Sidebar components */}
      {isLoading ? (
        <Box textAlign="center" py={10}>
          <Spinner size="xl" />
          <Text mt={4}>Loading your portfolio...</Text>
        </Box>
      ) : holdings.length > 0 ? (
        <>
          <PortfolioSummary holdings={holdings} />
          <HoldingsTable holdings={holdings} />
        </>
      ) : (
        <Box textAlign="center" py={10}>
          <Text fontSize="xl" mb={6}>Connect your brokerage account to view your portfolio.</Text>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
            {/* Connect Upstox */}
            <Box 
              border="1px solid" 
              borderColor={useColorModeValue('gray.200', 'gray.600')} 
              borderRadius="lg" 
              p={4}
              cursor="pointer"
              onClick={connectUpstox}
              _hover={{ bg: useColorModeValue('gray.50', 'gray.700') }}
            >
              <Image src='/upstox.png' alt="Upstox" boxSize="100px" mx="auto" mb={4} />
              <Text fontWeight="bold">Connect Upstox</Text>
            </Box>
            
            {/* Connect Groww (Coming Soon) */}
            <Box 
              border="1px solid" 
              borderColor={useColorModeValue('gray.200', 'gray.600')} 
              borderRadius="lg" 
              p={4}
              _hover={{ bg: useColorModeValue('gray.50', 'gray.700') }}
            >
              <Image src='/groww.png' alt="Groww" boxSize="100px" mx="auto" mb={4} />
              <Text fontWeight="bold">Connect Groww</Text>
              <Badge colorScheme="yellow" mt={2}>Coming Soon</Badge>
            </Box>
            
            {/* Connect Kite (Coming Soon) */}
            <Box 
              border="1px solid" 
              borderColor={useColorModeValue('gray.200', 'gray.600')} 
              borderRadius="lg" 
              p={4}
              _hover={{ bg: useColorModeValue('gray.50', 'gray.700') }}
            >
              <Image src='/kite.png' alt="Kite" boxSize="100px" mx="auto" mb={4} />
              <Text fontWeight="bold">Connect Kite</Text>
              <Badge colorScheme="yellow" mt={2}>Coming Soon</Badge>
            </Box>
          </SimpleGrid>
        </Box>
      )}

      {/* Stock Detail Modal */}
      <Modal 
        isOpen={!!selectedStock} 
        onClose={() => setSelectedStock(null)}
        size="xl"
      >
        <ModalOverlay />
        <ModalContent maxW="90vw">
          <ModalHeader>
            {selectedStock?.company_name} ({selectedStock?.trading_symbol})
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <CandleChart
              data={candleData}
              isLoading={isChartLoading}
              onIntervalChange={handleIntervalChange}
              selectedInterval={candleInterval}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Container>
  );
};
